import { AsyncPipe, CurrencyPipe, DatePipe, DecimalPipe, NgClass, UpperCasePipe } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { BlinkDirective } from '@zero/shared/ui-directives';
import { InstrumentDetailsLinkComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, IconComponent, PillComponent } from '@zero/shared/ui-web-design-system';
import { WatchlistVM } from '@zero/shared/util-global-state';
import { InstrumentType, WatchlistSettings } from '@zero/shared/util-models';
import { AuthService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'tr[watchlist-item]',
  templateUrl: './watchlist-item.component.html',
  styleUrls: ['./watchlist-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    BlinkDirective,
    NgClass,
    InstrumentDetailsLinkComponent,
    FormsModule,
    NgbDropdownModule,
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
    TranslocoPipe,
    ButtonComponent,
    IconComponent,
    PillComponent,
    UpperCasePipe,
    AsyncPipe,
  ],
})
export class WatchlistItemComponent implements AfterViewChecked {
  @Input() public item: WatchlistVM;
  @Input() public compressed = false;
  @Input() public performanceBasedOnEntry = false;
  @Input() public settings?: WatchlistSettings;
  @Input() public isEditEntryQuoteMode = false;
  @Input() public isCurrentlyEditing = false;
  @Input() public isFirst = false;
  @Input() public isLast = false;
  @Input() public isAddTagMode = false;
  @Input() public isCurrentlyAddTagMode = false;

  public selectedIsinCompressed: string = null;
  public currentEntryQuote?: number;
  public isEntryQuoteError = false;
  public readonly isCrypto = InstrumentType.Crypto;
  public readonly commonRoutes = commonRoutes;

  @ViewChild('entryQuote') public input: ElementRef;

  @Output() public removeItem = new EventEmitter<void>();
  @Output() public up = new EventEmitter<void>();
  @Output() public down = new EventEmitter<void>();
  @Output() public editNote = new EventEmitter<void>();
  @Output() public editEntryQuote = new EventEmitter<void>();
  @Output() public updateEntryQuote = new EventEmitter<number>();
  @Output() public addTagMode = new EventEmitter<void>();
  @Output() public cancelTagMode = new EventEmitter<void>();

  readonly #authService = inject(AuthService);

  public readonly isPotentialCustomer$ = this.#authService.isPotentialCustomer();

  ngAfterViewChecked(): void {
    if (this.isCurrentlyEditing && this.input) {
      const input = this.input.nativeElement as HTMLTextAreaElement;
      input.focus();
    }
  }

  public handleUp() {
    if (this.isFirst) {
      return;
    }
    this.up.emit();
  }

  public handleDown() {
    if (this.isLast) {
      return;
    }
    this.down.emit();
  }

  public selectIsinCompressed = (isin: string): void => {
    this.selectedIsinCompressed = this.selectedIsinCompressed !== isin ? isin : null;
  };

  public validateEntryQuoteAndUpdate = (): void => {
    if (!this.currentEntryQuote) {
      this.updateEntryQuote.emit(this.item.prevDayQuote);
      return;
    }
    const entryQuote = Number(this.currentEntryQuote?.toString().replace(',', '.'));
    this.isEntryQuoteError = isNaN(entryQuote) || this.currentEntryQuote < 0;
    !this.isEntryQuoteError && this.updateEntryQuote.emit(entryQuote);
  };
}
